import React, { useCallback, useState } from 'react';

import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { openOrCloseGlobalDialog } from '../../action/dialog/global-dialog';
import { createMergeIntegration } from '../actions/create-merge-integration';

import { post } from '../../api/api';
import { GLOBAL_DIALOGS } from '../../dialogs/global-dialogs-config';
import { UiButton } from '../../ui/ui-button';

import { MergeLink } from './merge-link';

type InputProps = {
  mergeIntegrationKey: string | null | undefined;
  className?: string;
  isIntegrated?: boolean;
  orgId: number;
  size?: 'default' | 'xxs' | 'xs' | 'sm' | 'lg';
  variant?: 'primary' | 'secondary' | 'tertiary' | 'dismiss' | 'contained';
  onExit?: () => void;
  disabled?: boolean;
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>, orgId: number) => ({
  actions: {
    createMergeIntegration: (publicToken: any) =>
      dispatch(createMergeIntegration(orgId, publicToken)),
    showGlobalErrorDialog: () =>
      dispatch(openOrCloseGlobalDialog(GLOBAL_DIALOGS.GLOBAL_ERROR.key, true)),
  },
});

export const MergeLinkButton = ({
  mergeIntegrationKey,
  className,
  isIntegrated,
  orgId,
  size,
  variant,
  onExit,
  disabled,
}: InputProps) => {
  const { actions } = mapDispatchToProps(useDispatch(), orgId);

  const [linkToken, setLinkToken] = useState<string | undefined>();

  const onSuccess = useCallback(
    (publicToken) => {
      try {
        actions.createMergeIntegration(publicToken);
      } catch {
        actions.showGlobalErrorDialog();
      }
    },
    [actions],
  );

  const handleClick = async () => {
    const response = await post('/merge/link-token', {
      body: JSON.stringify({ orgId, integration: mergeIntegrationKey }),
    });
    if (!response.ok) {
      actions.showGlobalErrorDialog();
    }

    const data = await response.json();
    setLinkToken(data.linkToken);
  };

  const getSmartlookClickInfo = () => ({
    eventName: 'HRIS response dialog click',
    props: {
      component: 'sync',
      action: isIntegrated ? 'reauthenticate' : 'setup',
    },
  });

  return (
    <>
      <UiButton
        variant={variant || 'secondary'}
        className={className}
        size={size}
        onClick={handleClick}
        disabled={disabled}
        smartlookEvent={getSmartlookClickInfo()}
      >
        {`${isIntegrated ? 'Reauthenticate' : 'Proceed With Setup'}`}
      </UiButton>
      {linkToken && <MergeLink linkToken={linkToken} onSuccess={onSuccess} onExit={onExit} />}
    </>
  );
};
